import React from "react";
import { createRoot } from "react-dom/client";
import SemiCircleSpinnerEmbed, { EmbedConfig, CropData } from "./SemiCircleSpinnerEmbed";

// Type declarations for the global namespace
declare global {
  interface Window {
    SemiCircleSpinner: {
      init: (elementId: string, config?: EmbedConfig) => void;
      initFromDataAttributes: (elementId: string) => void;
      unmount: (elementId: string) => void;
    };
  }
}

// Track mounted instances to prevent double-mounting
const mountedInstances = new Map<string, ReturnType<typeof createRoot>>();

// Collect crops from multiple data source elements
function collectCropsFromDataSource(selector: string): CropData[] {
  const crops: CropData[] = [];
  const dataElements = document.querySelectorAll(selector);
  
  dataElements.forEach((element) => {
    if (element instanceof HTMLElement) {
      const cropType = element.dataset.cropType;
      if (cropType) {
        crops.push({
          name: cropType,
          state: element.dataset.location || "",
          price: parseFloat(element.dataset.price || "0"),
          harvestYield: parseFloat(element.dataset.harvestYield || "0"),
          highYield: parseFloat(element.dataset.highYield || "0"),
          lowYield: element.dataset.lowYield ? parseFloat(element.dataset.lowYield) : undefined,
          cost: parseFloat(element.dataset.cost || "0"),
          avgYield: parseFloat(element.dataset.avgYield || "0"),
          landownerMsgGoodYield: element.dataset.landownerMsgGoodYield || undefined,
          landownerMsgLowYield: element.dataset.landownerMsgLowYield || undefined,
          farmerMsgGoodYield: element.dataset.farmerMsgGoodYield || undefined,
          farmerMsgLowYield: element.dataset.farmerMsgLowYield || undefined,
          insuranceBasePrice: element.dataset.insuranceBasePrice ? parseFloat(element.dataset.insuranceBasePrice) : undefined,
          insuranceHarvestPrice: element.dataset.insuranceHarvestPrice ? parseFloat(element.dataset.insuranceHarvestPrice) : undefined,
        });
      }
    }
  });
  
  return crops;
}

// Parse data attributes from container element
function parseDataAttributes(element: HTMLElement): EmbedConfig {
  const crops: CropData[] = [];

  // Check for data source selector (aggregates from multiple elements)
  const dataSource = element.dataset.source;
  if (dataSource) {
    const collectedCrops = collectCropsFromDataSource(dataSource);
    crops.push(...collectedCrops);
  }

  // Check for single crop data attributes (backward compatible)
  const cropType = element.dataset.cropType;
  if (cropType) {
    crops.push({
      name: cropType,
      state: element.dataset.location || "",
      price: parseFloat(element.dataset.price || "0"),
      harvestYield: parseFloat(element.dataset.harvestYield || "0"),
      highYield: parseFloat(element.dataset.highYield || "0"),
      lowYield: element.dataset.lowYield ? parseFloat(element.dataset.lowYield) : undefined,
      cost: parseFloat(element.dataset.cost || "0"),
      avgYield: parseFloat(element.dataset.avgYield || "0"),
      landownerMsgGoodYield: element.dataset.landownerMsgGoodYield || undefined,
      landownerMsgLowYield: element.dataset.landownerMsgLowYield || undefined,
      farmerMsgGoodYield: element.dataset.farmerMsgGoodYield || undefined,
      farmerMsgLowYield: element.dataset.farmerMsgLowYield || undefined,
      insuranceBasePrice: element.dataset.insuranceBasePrice ? parseFloat(element.dataset.insuranceBasePrice) : undefined,
      insuranceHarvestPrice: element.dataset.insuranceHarvestPrice ? parseFloat(element.dataset.insuranceHarvestPrice) : undefined,
    });
  }

  // Check for JSON crops data
  const cropsJson = element.dataset.crops;
  if (cropsJson) {
    try {
      const parsedCrops = JSON.parse(cropsJson);
      if (Array.isArray(parsedCrops)) {
        crops.push(...parsedCrops);
      }
    } catch (e) {
      console.error("SemiCircleSpinner: Failed to parse crops JSON", e);
    }
  }

  return {
    crops: crops.length > 0 ? crops : undefined,
    primaryColor: element.dataset.primaryColor,
    showInsurance: element.dataset.showInsurance !== "false",
  };
}

// Initialize spinner with config object
function init(elementId: string, config?: EmbedConfig) {
  const container = document.getElementById(elementId);
  if (!container) {
    console.error(`SemiCircleSpinner: Element with id "${elementId}" not found`);
    return;
  }

  // Prevent double-mounting
  if (mountedInstances.has(elementId)) {
    console.warn(`SemiCircleSpinner: "${elementId}" already mounted. Use unmount() first to re-initialize.`);
    return;
  }

  const root = createRoot(container);
  mountedInstances.set(elementId, root);
  root.render(
    React.createElement(SemiCircleSpinnerEmbed, { config })
  );
}

// Initialize spinner from data attributes on container element
function initFromDataAttributes(elementId: string) {
  const container = document.getElementById(elementId);
  if (!container) {
    console.error(`SemiCircleSpinner: Element with id "${elementId}" not found`);
    return;
  }

  const config = parseDataAttributes(container);
  init(elementId, config);
}

// Unmount a spinner instance for cleanup
function unmount(elementId: string) {
  const root = mountedInstances.get(elementId);
  if (root) {
    root.unmount();
    mountedInstances.delete(elementId);
  }
}

// Expose global API
window.SemiCircleSpinner = {
  init,
  initFromDataAttributes,
  unmount,
};

// Auto-initialize any elements with data-semicircle-spinner attribute
document.addEventListener("DOMContentLoaded", () => {
  const autoInitElements = document.querySelectorAll("[data-semicircle-spinner]");
  autoInitElements.forEach((element) => {
    if (element.id) {
      initFromDataAttributes(element.id);
    } else {
      // Generate an ID if none exists
      const id = `semicircle-spinner-${Math.random().toString(36).substr(2, 9)}`;
      element.id = id;
      initFromDataAttributes(id);
    }
  });
});

export { init, initFromDataAttributes, unmount };
